import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { accountService, Profile } from "../services/account.service";
import { tagOf } from "../utils/api-error";

const errKey = (e: unknown): string =>
  ({ invalid_password: "account.invalidPassword", weak_password: "account.weakPassword",
     email_taken: "account.emailTaken", invalid_otp: "account.invalidOtp",
     expired_otp: "account.invalidOtp", rate_limited: "account.rateLimited" } as Record<string, string>)[tagOf(e) ?? ""] ?? "account.genericError";

export default function ProfileSettings() {
  const { t } = useTranslation(["common"]);
  const [profile, setProfile] = useState<Profile | null>(null);

  // profile form — icon is uploaded via its own endpoint, so it lives on
  // `profile`, not in this form's editable fields.
  const [form, setForm] = useState({ firstName: "", lastName: "", gender: "" });
  const [profileMsg, setProfileMsg] = useState<string>("");

  // icon upload
  const [iconMsg, setIconMsg] = useState<string>("");
  const [iconBusy, setIconBusy] = useState(false);

  // password form
  const [pw, setPw] = useState({ currentPassword: "", newPassword: "" });
  const [pwMsg, setPwMsg] = useState<string>("");

  // email form (two-step)
  const [email, setEmail] = useState({ newEmail: "", password: "", otp: "" });
  const [emailStep, setEmailStep] = useState<"request" | "verify">("request");
  const [emailMsg, setEmailMsg] = useState<string>("");

  useEffect(() => {
    accountService.getProfile().then((p) => {
      setProfile(p);
      setForm({
        firstName: p.firstName ?? "",
        lastName: p.lastName ?? "",
        // An account still stored as "other" has no matching <option>, so the
        // select renders blank while state holds a value the API now rejects.
        // Editing any other field would then 400. Start blank instead.
        gender: p.gender === "other" ? "" : (p.gender ?? ""),
      });
    });
  }, []);

  const saveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileMsg("");
    try {
      const updated = await accountService.updateProfile({
        firstName: form.firstName, lastName: form.lastName, gender: form.gender || null,
      });
      setProfile(updated);
      setProfileMsg(t("account.saved"));
    } catch (err) { setProfileMsg(t(errKey(err))); }
  };

  const uploadIcon = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // let the same file be re-picked after an error
    if (!file) return;
    setIconMsg("");
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) { setIconMsg(t("account.iconType")); return; }
    if (file.size > 10 * 1024 * 1024) { setIconMsg(t("account.iconTooBig")); return; }
    setIconBusy(true);
    try {
      setProfile(await accountService.uploadIcon(file));
      setIconMsg(t("account.saved"));
    } catch (err) { setIconMsg(t(errKey(err))); }
    finally { setIconBusy(false); }
  };

  const removeIcon = async () => {
    setIconMsg("");
    setIconBusy(true);
    try {
      setProfile(await accountService.removeIcon());
      setIconMsg(t("account.saved"));
    } catch (err) { setIconMsg(t(errKey(err))); }
    finally { setIconBusy(false); }
  };

  const savePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwMsg("");
    try {
      await accountService.changePassword(pw);
      setPw({ currentPassword: "", newPassword: "" });
      setPwMsg(t("account.passwordChanged"));
    } catch (err) { setPwMsg(t(errKey(err))); }
  };

  const requestEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailMsg("");
    try {
      await accountService.requestEmailChange({ newEmail: email.newEmail, password: email.password });
      setEmailStep("verify");
      setEmailMsg(t("account.codeSent"));
    } catch (err) { setEmailMsg(t(errKey(err))); }
  };

  const verifyEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailMsg("");
    try {
      await accountService.verifyEmailChange({ otp: email.otp });
      setEmailStep("request");
      setEmail({ newEmail: "", password: "", otp: "" });
      setProfile((p) => (p ? { ...p, email: email.newEmail } : p));
      setEmailMsg(t("account.emailChanged"));
    } catch (err) { setEmailMsg(t(errKey(err))); }
  };

  if (!profile) return null;

  return (
    <section className="space-y-8">
      <div className="card p-7">
        <form onSubmit={saveProfile} className="space-y-3">
          <h2 className="font-display text-lg font-extrabold">{t("account.profileTitle")}</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="firstName" className="mb-1 block text-sm font-bold">{t("account.firstName")}</label>
              <input id="firstName" className="field-input" value={form.firstName}
                     onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
            </div>
            <div>
              <label htmlFor="lastName" className="mb-1 block text-sm font-bold">{t("account.lastName")}</label>
              <input id="lastName" className="field-input" value={form.lastName}
                     onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
            </div>
          </div>
          <label htmlFor="gender" className="mb-1 block text-sm font-bold">{t("account.gender")}</label>
          <select id="gender" className="field-input" value={form.gender}
                  onChange={(e) => setForm({ ...form, gender: e.target.value })}>
            <option value="">—</option>
            <option value="male">{t("account.genderMale")}</option>
            <option value="female">{t("account.genderFemale")}</option>
            {/* "other" hidden by request. The `account.genderOther` locale key
                is kept so re-enabling is this one line. Accounts already
                stored as "other" keep that value until the user picks again. */}
          </select>
          <span className="mb-1 block text-sm font-bold">{t("account.profileIcon")}</span>
          <div className="flex items-center gap-4">
            <span className="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-full border border-hairline bg-sand text-ink-soft">
              {profile.profileIcon ? (
                <img src={profile.profileIcon} alt={t("account.profileIcon")}
                     className="h-full w-full object-cover" />
              ) : (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10zm0 2c-4.4 0-8 2.7-8 6v2h16v-2c0-3.3-3.6-6-8-6z" />
                </svg>
              )}
            </span>
            <div className="space-y-1">
              <label htmlFor="profileIcon"
                     className={`btn-ghost cursor-pointer px-4 py-2 text-sm ${iconBusy ? "pointer-events-none opacity-50" : ""}`}>
                {iconBusy ? t("account.iconUploading") : t("account.changeIcon")}
              </label>
              {profile.profileIcon && (
                <button type="button" onClick={removeIcon} disabled={iconBusy}
                        className="btn-ghost ml-2 px-4 py-2 text-sm disabled:opacity-50">
                  {t("account.removeIcon")}
                </button>
              )}
              <p className="text-xs text-ink-soft">{t("account.iconHint")}</p>
            </div>
            <input id="profileIcon" type="file" accept="image/png,image/jpeg,image/webp"
                   aria-label={t("account.profileIcon")} disabled={iconBusy}
                   onChange={uploadIcon} className="sr-only" />
          </div>
          {iconMsg && <p role="status" className="text-sm text-teal-dark">{iconMsg}</p>}
          <button type="submit" className="btn">{t("account.save")}</button>
          {profileMsg && <p role="status" className="text-sm text-teal-dark">{profileMsg}</p>}
        </form>
      </div>

      <div className="card p-7">
        <form onSubmit={savePassword} className="space-y-3">
          <h2 className="font-display text-lg font-extrabold">{t("account.passwordTitle")}</h2>
          <label htmlFor="currentPassword" className="mb-1 block text-sm font-bold">{t("account.currentPassword")}</label>
          <input id="currentPassword" type="password" className="field-input" value={pw.currentPassword}
                 onChange={(e) => setPw({ ...pw, currentPassword: e.target.value })} />
          <label htmlFor="newPassword" className="mb-1 block text-sm font-bold">{t("account.newPassword")}</label>
          <input id="newPassword" type="password" className="field-input" value={pw.newPassword}
                 onChange={(e) => setPw({ ...pw, newPassword: e.target.value })} />
          <button type="submit" className="btn">{t("account.save")}</button>
          {pwMsg && <p role="status" className="text-sm text-teal-dark">{pwMsg}</p>}
        </form>
      </div>

      <div className="card p-7">
        <div className="space-y-3">
          <h2 className="font-display text-lg font-extrabold">{t("account.emailTitle")}</h2>
          {emailStep === "request" ? (
            <form onSubmit={requestEmail} className="space-y-3">
              <label htmlFor="newEmail" className="mb-1 block text-sm font-bold">{t("account.newEmail")}</label>
              <input id="newEmail" type="email" className="field-input" value={email.newEmail}
                     onChange={(e) => setEmail({ ...email, newEmail: e.target.value })} />
              <label htmlFor="emailPassword" className="mb-1 block text-sm font-bold">{t("account.emailPassword")}</label>
              <input id="emailPassword" type="password" className="field-input" value={email.password}
                     onChange={(e) => setEmail({ ...email, password: e.target.value })} />
              <button type="submit" className="btn">{t("account.sendCode")}</button>
            </form>
          ) : (
            <form onSubmit={verifyEmail} className="space-y-3">
              <label htmlFor="otp" className="mb-1 block text-sm font-bold">{t("account.otp")}</label>
              <input id="otp" className="field-input tracking-[.4em] text-center font-display text-lg" value={email.otp}
                     onChange={(e) => setEmail({ ...email, otp: e.target.value })} />
              <button type="submit" className="btn">{t("account.confirmEmail")}</button>
            </form>
          )}
          {emailMsg && <p role="status" className="text-sm text-teal-dark">{emailMsg}</p>}
        </div>
      </div>
    </section>
  );
}
